import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import { datePickerClearableProps, datePickerFormItemProps, selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Checkbox, Col, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useMarkAllNotificationsAsSeen } from "../../../../common/api/queries";
import { rowGutter } from "../../../../common/constants";
import { useIsSystemAdmin, usePermissions } from "../../../auth/hooks";
import { DEFAULT_FILTER_NOTIFICATIONS_QUERY_PARAMS } from "../../api";
import { NotificationFilterPageRequest } from "../../types";
import { getAvailableNotificationTopics } from "../../utils";

const colSpan = 4;

interface Props {
  searchParams: NotificationFilterPageRequest;
}

const NotificationPageFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm<NotificationFilterPageRequest>();
  const sentAtMax = Form.useWatch("sentAtMax", form);
  const sentAtMin = Form.useWatch("sentAtMin", form);

  const permissions = usePermissions();
  const isCurrentUserSystemAdmin = useIsSystemAdmin();

  const markAllNotificationsAsSeen = useMarkAllNotificationsAsSeen();

  const availableTopics = useMemo(
    () => getAvailableNotificationTopics(permissions, isCurrentUserSystemAdmin),
    [permissions, isCurrentUserSystemAdmin],
  );

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      topics: searchParams.topics ? searchParams.topics.filter((topic) => availableTopics.includes(topic)) : [],
    });
  }, [searchParams, form, availableTopics]);

  const handleFilterSubmit = (filter: NotificationFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_FILTER_NOTIFICATIONS_QUERY_PARAMS),
            pageIndex: undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("notification.titles.pageTitle")}</h2>}
      extra={
        <SecondaryButton icon={<AntIcon type="eye" />} onClick={() => markAllNotificationsAsSeen.mutate()}>
          {t("notification.actions.markAllAsSeen")}
        </SecondaryButton>
      }
    >
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="notificationSearchForm">
        <Row gutter={rowGutter}>
          <Col span={5}>
            <Form.Item
              name="keyword"
              label={t("common.search")}
              rules={[
                validations.size(
                  validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                  validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                ),
              ]}
            >
              <Input allowClear placeholder={t("notification.helpers.searchHint")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="topics"
              label={t("notification.enums.notificationTopic._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                options={availableTopics.map((topic) => ({
                  value: topic,
                  label: t("notification.enums.notificationTopic." + topic),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="sentAtMin"
              label={t("notification.filter.sentAtMin")}
              rules={[
                sentAtMax ? validations.notAfter(sentAtMax, t("notification.filter.sentAtMax")) : validations.notFuture,
              ]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerClearableProps} maxDate={dayjs(sentAtMax)} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="sentAtMax"
              label={t("notification.filter.sentAtMax")}
              rules={[
                validations.notFuture,
                ...(sentAtMin ? [validations.notBefore(sentAtMin, t("notification.filter.sentAtMin"))] : []),
              ]}
              {...datePickerFormItemProps}
            >
              <DatePicker
                {...datePickerClearableProps}
                minDate={sentAtMin ? dayjs(sentAtMin) : undefined}
                maxDate={dayjs()}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="onlyUnseen"
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("notification.filter.onlyUnseen")}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={3} className="no-title-space right-align">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default NotificationPageFilterForm;
